/**
 * Reading a project, offline first.
 *
 * A switchroom has no signal. The app must open, show the board, show the
 * script, and show what is still outstanding, with nothing but what it stored
 * last time — and it must never pretend. Three rules follow:
 *
 *  1. **Cached data is served immediately**, before any network call.
 *  2. **A refresh that fails leaves the cached answer standing**, marked stale.
 *     A blank screen because the network went is worse than yesterday's script
 *     clearly labelled as yesterday's.
 *  3. **No cache and no network is an honest empty**, with a reason. Never an
 *     empty list that looks like "this board has no breakers".
 *
 * The distinction in (3) is the one that matters on site: a lineup showing zero
 * breakers because the phone cannot reach SharePoint looks exactly like a lineup
 * that is genuinely finished.
 */

import type { ProjectScope } from '../api/sp.js';
import { LIST } from '../api/sp.js';
import type { ReadCache } from '../db/local.js';
import type { Asset, ShotProfile, TemplateItem, TemplateSection } from '../domain/types.js';
import { mapAll, toAsset, toItem, toSection, toShotProfile, type SpRow } from './mappers.js';

export type Freshness = 'live' | 'cached' | 'stale' | 'empty';

export interface Loaded<T> {
  data: T;
  freshness: Freshness;
  /** When the served data was stored. Absent for a live read. */
  storedAt?: string;
  /** Why it is not live — shown to the engineer, never swallowed. */
  reason?: string;
  /** Rows the server sent that could not be mapped. */
  rejected?: { row: SpRow; reason: string }[];
}

export interface Script {
  sections: TemplateSection[];
  items: TemplateItem[];
}

export interface Register {
  /** Parent equipment: boards, MCCs, gensets. */
  boards: Asset[];
  /** Children, grouped by their parent's tag. */
  breakersByBoard: Record<string, Asset[]>;
}

interface CacheEnvelope<T> {
  storedAt: string;
  data: T;
}

const isOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * The shared shape: try the cache, try the network, prefer live, never lose what
 * we had.
 */
async function cacheFirst<T>(
  cache: ReadCache,
  key: string,
  fetchLive: () => Promise<{ data: T; rejected: { row: SpRow; reason: string }[] }>,
): Promise<Loaded<T>> {
  const cached = await cache.get<CacheEnvelope<T>>(key);

  if (isOffline()) {
    return cached
      ? { data: cached.data, freshness: 'cached', storedAt: cached.storedAt }
      : { data: undefined as T, freshness: 'empty', reason: 'no connection and nothing stored for this project yet' };
  }

  try {
    const live = await fetchLive();
    await cache.set(key, { storedAt: new Date().toISOString(), data: live.data } satisfies CacheEnvelope<T>);
    return live.rejected.length
      ? { data: live.data, freshness: 'live', rejected: live.rejected }
      : { data: live.data, freshness: 'live' };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    if (cached) {
      // Yesterday's script, clearly labelled, beats a blank screen.
      return { data: cached.data, freshness: 'stale', storedAt: cached.storedAt, reason };
    }
    return { data: undefined as T, freshness: 'empty', reason };
  }
}

/** The approved script for this project: its own copy, never the library's. */
export async function loadScript(scope: ProjectScope, cache: ReadCache, templateId: number): Promise<Loaded<Script>> {
  return cacheFirst<Script>(cache, `script:${templateId}`, async () => {
    const sectionRows = await scope.list<SpRow>(LIST.sections, {
      filter: `TemplateId eq ${templateId}`,
      orderBy: 'Seq',
      select: ['Id', 'Title', 'Seq', 'TemplateId', 'SectionDescription', 'ApplicableByDefault'],
    });
    const sections = mapAll(sectionRows, toSection);

    const ids = sections.rows.map((s) => s.id);
    // A template with no sections means an empty script, not "fetch everything":
    // an unfiltered item read would pull in every other project's checks.
    const itemRows = ids.length
      ? await scope.list<SpRow>(LIST.items, {
          filter: ids.map((id) => `SectionId eq ${id}`).join(' or '),
          orderBy: 'Seq',
          select: [
            'Id', 'Title', 'Seq', 'SectionId', 'ResponseType', 'ItemDescription', 'ExpectedReaction',
            'AcceptanceCriteria', 'Unit', 'Nominal', 'Tolerance', 'ToleranceMode', 'RequiresPhoto', 'ShotType',
          ],
        })
      : [];
    const items = mapAll(itemRows, toItem);

    return {
      data: { sections: sections.rows, items: items.rows },
      rejected: [...sections.rejected, ...items.rejected],
    };
  });
}

/** Boards and their breakers, in one pass. */
export async function loadRegister(scope: ProjectScope, cache: ReadCache): Promise<Loaded<Register>> {
  return cacheFirst<Register>(cache, 'register', async () => {
    const rows = await scope.list<SpRow>(LIST.assets, {
      orderBy: 'Title',
      select: [
        'Id', 'Title', 'ProjectId', 'EquipmentType', 'BreakerType', 'ParentAssetTag', 'Manufacturer',
        'Model', 'SerialNumber', 'AssetLocation', 'QRPayload', 'RequiredShotCount',
      ],
    });
    const mapped = mapAll(rows, toAsset);

    const boards = mapped.rows.filter((a) => !a.parentAssetTag);
    const breakersByBoard: Record<string, Asset[]> = {};
    for (const a of mapped.rows) {
      if (!a.parentAssetTag) continue;
      (breakersByBoard[a.parentAssetTag] ??= []).push(a);
    }
    return { data: { boards, breakersByBoard }, rejected: mapped.rejected };
  });
}

/** Shot profiles: this project's, or the library's where it has none of its own. */
export async function loadShotProfiles(scope: ProjectScope, cache: ReadCache): Promise<Loaded<ShotProfile[]>> {
  return cacheFirst<ShotProfile[]>(cache, 'shotProfiles', async () => {
    const rows = await scope.listWithLibrary<SpRow>(LIST.shotProfiles, {
      orderBy: 'Seq',
      select: ['Id', 'Title', 'BreakerType', 'ShotGroup', 'MeasurementGroup', 'Viewpoint', 'Seq', 'Required', 'ShotDescription', 'ProjectId'],
    });
    const mapped = mapAll(rows, toShotProfile);

    // A project that defines its own profiles for a breaker type replaces the
    // library's for that type — it does not add to them, or an ACB would need
    // both sets photographed.
    const ownTypes = new Set(
      rows.filter((r) => r.ProjectId !== null && r.ProjectId !== undefined)
        .map((r) => String(r.BreakerType)),
    );
    const data = ownTypes.size
      ? mapped.rows.filter((p, i) => {
          const isOwn = rows[i]?.ProjectId !== null && rows[i]?.ProjectId !== undefined;
          return isOwn || !ownTypes.has(p.breakerType);
        })
      : mapped.rows;

    return { data, rejected: mapped.rejected };
  });
}

/** True when the app is showing something it cannot vouch for right now. */
export const needsWarning = (f: Freshness): boolean => f === 'stale' || f === 'empty';

/** One line an engineer can act on. */
export function freshnessLabel(l: Loaded<unknown>): string {
  switch (l.freshness) {
    case 'live':
      return 'Up to date';
    case 'cached':
      return `Offline — showing what was stored${l.storedAt ? ` at ${l.storedAt.slice(11, 16)}` : ''}`;
    case 'stale':
      return `Could not refresh — showing what was stored${l.storedAt ? ` at ${l.storedAt.slice(11, 16)}` : ''}`;
    case 'empty':
      return `Nothing to show: ${l.reason ?? 'no data'}`;
  }
}
